import { useEffect, Component } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { scrollToId } from "@/lib/scroll";
import Admin from "@/pages/Admin";
import Article from "@/pages/Article";
import ManageBooking from "@/pages/ManageBooking";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import TrustMarquee from "@/components/TrustMarquee";
import BrandPanorama from "@/components/BrandPanorama";
import Services from "@/components/Services";
import WhyAiCendor from "@/components/WhyAiCendor";
import Approach from "@/components/Approach";
import FounderCta from "@/components/FounderCta";
import Insights from "@/components/Insights";
import Booking from "@/components/booking/Booking";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ink px-6 text-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">AiCendor</p>
            <p className="mt-4 font-display text-2xl">Something went wrong.</p>
            <button
              data-testid="error-reload-button"
              onClick={() => window.location.reload()}
              className="mt-6 border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-widest transition-colors hover:border-gold"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const Landing = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => scrollToId(hash.slice(1)), 350);
    return () => clearTimeout(t);
  }, [hash]);
  return (
  <div className="relative min-h-screen bg-ink text-white">
    <div className="noise-overlay" aria-hidden="true" />
    <Nav />
    <main>
      <Hero />
      <TrustMarquee />
      <BrandPanorama />
      <Services />
      <WhyAiCendor />
      <Approach />
      <Insights />
      <FounderCta />
      <Booking />
      <Contact />
    </main>
    <Footer />
  </div>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/insights/:slug" element={<Article />} />
            <Route path="/booking/:token" element={<ManageBooking />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </AuthProvider>
        <Toaster position="bottom-right" theme="dark" />
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
